/**
 * "Nieuwste eerst"-volgorde voor producten, op één plek.
 *
 * Waarom een eigen helper: `createdAt`/`publishedAt` komen in drie vormen binnen,
 * afhankelijk van waar het product vandaan komt — een client-Firestore-`Timestamp`
 * (categorie-/merkpagina's, /shop op de client), een getal in ms (door de server
 * geserialiseerd, app/shop/page.tsx) of een ISO-string/Date (admin-API). Het type
 * zegt `Date`, maar `new Date(<Timestamp>)` geeft in V8 een Invalid Date (NaN):
 * de sortering deed dan niets en de lijst bleef in document-ID-volgorde staan.
 *
 * Bewust zonder imports: pure functies, bruikbaar op server én client.
 */

/** Zet een datumwaarde (Timestamp, {seconds}, getal, ISO-string, Date) om naar ms; `null` als het er niet is of niet te lezen valt. */
export function toMillis(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v === "string") {
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v === "object") {
    // Client-SDK Timestamp: toMillis()/toDate() + seconds/nanoseconds. Admin-SDK:
    // idem, met _seconds/_nanoseconds. Een JSON-geserialiseerde Timestamp heeft
    // alleen nog de kale velden.
    const o = v as {
      toMillis?: () => number;
      toDate?: () => Date;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };
    if (typeof o.toMillis === "function") {
      const t = o.toMillis();
      return Number.isFinite(t) ? t : null;
    }
    if (typeof o.toDate === "function") {
      const t = o.toDate().getTime();
      return Number.isNaN(t) ? null : t;
    }
    const s = typeof o.seconds === "number" ? o.seconds : o._seconds;
    if (typeof s === "number" && Number.isFinite(s)) {
      const ns = typeof o.nanoseconds === "number" ? o.nanoseconds : o._nanoseconds;
      return s * 1000 + Math.floor((typeof ns === "number" ? ns : 0) / 1e6);
    }
  }
  return null;
}

type MetDatums = { id?: string; createdAt?: unknown; publishedAt?: unknown };

/**
 * Het "toegevoegd"-moment van een product: `createdAt`, of — als er een
 * publicatiedatum is die al verstreken is en later valt — dát moment. Een vooraf
 * aangemaakt of ingepland product hoort bovenaan te staan vanaf de dag dat het
 * live ging, niet op de dag dat het in de admin werd aangemaakt. Een nog niet
 * verstreken `publishedAt` telt niet mee (zo'n product is publiek niet zichtbaar).
 *
 * Er is geen datum per kleur: een nieuwe kleur van een oud product stijgt dus niet
 * mee, ook niet met `isNew` — "nieuwste eerst" betekent "als product toegevoegd".
 */
export function productAddedMs(p: MetDatums, now: number = Date.now()): number {
  const created = toMillis(p.createdAt) ?? 0;
  const published = toMillis(p.publishedAt);
  return published !== null && published <= now ? Math.max(created, published) : created;
}

/**
 * Comparator voor `Array.prototype.sort`: nieuwste eerst. Bij gelijke tijd (veel
 * producten komen uit dezelfde import-seconde) een vaste volgorde op id, zodat de
 * lijst voorspelbaar is in plaats van afhankelijk van de volgorde waarin Firestore
 * de documenten leverde.
 */
export function compareNewest(a: MetDatums, b: MetDatums, now: number = Date.now()): number {
  const d = productAddedMs(b, now) - productAddedMs(a, now);
  if (d !== 0) return d;
  const ia = a.id ?? "";
  const ib = b.id ?? "";
  return ia < ib ? -1 : ia > ib ? 1 : 0;
}

// ───────────────────────── Kaarten spreiden per product ─────────────────────────
//
// In het overzicht is er één kaart per KLEUR van een product. Staan de kaarten van
// één product direct achter elkaar, dan vult een product met veel kleuren een hele
// pagina: Cavello heren boxershort (2-pack) heeft 33 kleuren en was op heren-
// ondergoed zowel het nieuwste als het bestverkochte product, dus "Nieuwste eerst"
// toonde 30 keer hetzelfde product (feedback 14-10-2026).
//
// Oplossing: ronde 1 = de eerste kleur van elk product (in de gekozen productvolgorde),
// ronde 2 = de tweede kleuren, enzovoort. De volgorde van de producten blijft binnen
// elke ronde precies zoals de sortering hem bepaalde; alleen de kleuren worden
// uitgesteld. Zo laat pagina 1 30 verschillende producten zien i.p.v. er één.
//
// Alleen voor sorteringen waarbij de volgorde van producten er "los van de prijs"
// toe doet: bij een prijssortering moeten gelijke prijzen juist bij elkaar blijven
// staan, anders is de lijst niet meer oplopend.

/** Sorteringen waarvoor de kaarten gespreid worden (zie boven). */
const SPREIDENDE_SORTERINGEN = ["relevance", "newest"];

export function shouldSpreadCards(sort: string | undefined | null): boolean {
  return SPREIDENDE_SORTERINGEN.includes(sort || "relevance");
}

/**
 * Herordent kaarten (die per product al achter elkaar staan, in de gewenste
 * productvolgorde) in rondes: eerst de 1e kaart van elk product, dan de 2e, enz.
 * Stabiel en zonder kaarten te verliezen of te dupliceren; een kaart zonder
 * product-id telt als een eigen product.
 */
export function spreadCardsPerProduct<T extends { product: { id?: string } }>(cards: T[]): T[] {
  if (cards.length < 2) return cards;
  const groepen = new Map<string, T[]>();
  cards.forEach((kaart, i) => {
    const sleutel = kaart.product.id ?? `__zonder-id-${i}`;
    const groep = groepen.get(sleutel);
    if (groep) groep.push(kaart);
    else groepen.set(sleutel, [kaart]);
  });
  // Elk product heeft maar één kaart: niets te spreiden, dezelfde array teruggeven.
  if (groepen.size === cards.length) return cards;

  const lijsten = [...groepen.values()];
  const rondes = Math.max(...lijsten.map((l) => l.length));
  const uit: T[] = [];
  for (let ronde = 0; ronde < rondes; ronde++) {
    for (const lijst of lijsten) {
      if (ronde < lijst.length) uit.push(lijst[ronde]);
    }
  }
  return uit;
}
